import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";
import heartLoading from "../../../assests/heartLoading.gif";

const PAGE_SIZE = 20;

export const DQResult = ({ results }) => {
  const [totalRecords, setTotalRecords]         = useState(0);
  const [completedRecords, setCompletedRecords] = useState(0);
  const [qualityScore, setQualityScore]         = useState(0);
  const [notRunRecords, setNotRunRecords]       = useState(0);
  const [passedRecords, setPassedRecords]       = useState(0);
  const [weightage, setWeightage]               = useState(null);
  const [chartData, setChartData]               = useState(null);

  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder]   = useState("asc");
  const [columnSearch, setColumnSearch] = useState({
    column_name:      "",
    is_cde:           "",
    rule_description: "",
    dimension_name:   "",
    keywords:         "",
    dq_result:        "",
    quality_score:    "",
  });
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const tableScrollRef = useRef(null);
  const sentinelRef    = useRef(null);
  const hasMoreRef     = useRef(false);
  const totalFilteredRef = useRef(0);

  const scrollTable = (direction) => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({
        left: direction === "left" ? -200 : 200,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (results) {
      const total  = results.rules?.length || 0;
      const failed = results.rules?.filter((r) => r.dq_result === "fail" || r.dq_result === "failure")?.length || 0;
      const notRun = results.rules?.filter((r) => ["not_run", "NOT_RUN", "Not Run"].includes(r.dq_result))?.length || 0;
      const passed = results.rules?.filter((r) => r.dq_result === "pass")?.length || 0;
      const score  = results.latest_dimension_scores?.score || 0;
      const dimW   = results.latest_dimension_scores || {};
      const chart  = results?.score_trend_plot_config;
      const updatedW = { ...dimW };
      delete updatedW.score;

      setTotalRecords(total);
      setCompletedRecords(failed);
      setNotRunRecords(notRun);
      setPassedRecords(passed);
      setQualityScore(score);
      setWeightage(updatedW);
      setChartData(chart);
    }
  }, [results]);

  const handleSort = (column) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const handleColumnSearchChange = (column, value) => {
    setColumnSearch((prev) => ({ ...prev, [column]: value }));
  };

  const filteredAndSortedData = useMemo(() => {
    if (!results?.rules || !Array.isArray(results.rules)) return [];
    let filtered = [...results.rules];

    Object.entries(columnSearch).forEach(([column, searchValue]) => {
      if (searchValue?.trim()) {
        filtered = filtered.filter((row) => {
          let val = "";
          if (column === "is_cde") {
            val = (row.is_cde === "N/A" || row.is_cde === false) ? "no" : "yes";
          } else if (column === "column_name") {
            val = row.check_type === "Asset Level Check" ? "asset level rule" : (row.column_name ?? "");
          } else {
            val = row[column] ?? "";
          }
          return String(val).toLowerCase().includes(searchValue.toLowerCase().trim());
        });
      }
    });

    if (sortColumn) {
      filtered.sort((a, b) => {
        let aVal = sortColumn === "is_cde"
          ? ((a.is_cde === "N/A" || a.is_cde === false) ? "no" : "yes")
          : (a[sortColumn] ?? "");
        let bVal = sortColumn === "is_cde"
          ? ((b.is_cde === "N/A" || b.is_cde === false) ? "no" : "yes")
          : (b[sortColumn] ?? "");
        const cmp = String(aVal).toLowerCase().localeCompare(String(bVal).toLowerCase());
        return sortOrder === "asc" ? cmp : -cmp;
      });
    }
    return filtered;
  }, [results, columnSearch, sortColumn, sortOrder]);

  // Reset visible count and update total ref whenever filtered set changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    totalFilteredRef.current = filteredAndSortedData.length;
  }, [filteredAndSortedData]);

  const currentData = filteredAndSortedData.slice(0, visibleCount);
  const hasMore = visibleCount < filteredAndSortedData.length;

  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

  const handleIntersect = useCallback((entries) => {
    if (entries[0].isIntersecting && hasMoreRef.current) {
      setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, totalFilteredRef.current));
    }
  }, []);

  useEffect(() => {
    const el = sentinelRef.current;
    const container = tableScrollRef.current;
    if (!el || !container) return;
    const observer = new IntersectionObserver(handleIntersect, {
      root: container,
      rootMargin: "100px",
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleIntersect]);

  const transformChartData = (plotlyData) => {
    if (!plotlyData?.data?.[0]) return [];
    const xData = plotlyData.data[0].x || [];
    const yData = plotlyData.data[0].y || [];
    return xData.map((date, i) => ({
      date,
      displayDate: new Date(date).toLocaleDateString("en-US", { day: "numeric", month: "short" }),
      score: yData[i],
    }));
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
      return (
        <div style={{ backgroundColor: "white", padding: "8px 12px", border: "1px solid #ccc", borderRadius: "4px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
          <p style={{ margin: 0, fontWeight: 600, fontSize: "12px" }}>Date: {payload[0].payload.date}</p>
          <p style={{ margin: 0, fontSize: "12px", color: "#0F766E" }}>Score: {payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  const getQualityScoreColor = (score) => {
    if (score > 85)  return "#4CAF50";
    if (score >= 55) return "#FF9800";
    return "#F44336";
  };

  const getStatusBadge = (dq_result) => {
    const isPass   = dq_result === "pass";
    const isFail   = dq_result === "fail" || dq_result === "FAILURE";
    const isNotRun = ["not_run", "NOT_RUN", "Not Run"].includes(dq_result);
    const label = isPass ? "Pass" : isFail ? "Fail" : isNotRun ? "Not Run" : "N/A";
    const cls   = isPass
      ? "bg-green-100 text-green-700"
      : isFail
      ? "bg-red-100 text-red-700"
      : isNotRun
      ? "bg-amber-100 text-amber-700"
      : "bg-gray-100 text-gray-600";
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase ${cls}`}>
        {label}
      </span>
    );
  };

  const columns = [
    { key: "column_name",      label: "Column Name"      },
    { key: "is_cde",           label: "CDE"              },
    { key: "rule_description", label: "Rule Description" },
    { key: "dimension_name",   label: "Dimension"        },
    { key: "keywords",         label: "Keywords"         },
    { key: "dq_result",        label: "Status"           },
    { key: "quality_score",    label: "Score"            },
    { key: "arguments",        label: "Arguments"        },
    { key: "sql",              label: "Sql", minWidth: "180px" },
  ];

  const SimpleStatCard = ({ title, value, color = "#1E88E5", isDynamic = false }) => (
    <Box sx={{ backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", padding: 3, width: "200px", height: "120px", display: "flex", flexDirection: "column", justifyContent: "space-between", border: "1px solid #d3d3d3", transition: "all 0.3s ease", cursor: "pointer", "&:hover": { boxShadow: "0 12px 30px rgba(0,0,0,0.2) !important", transform: "translateY(-6px) !important", border: "1px solid #bb1ccc !important", backgroundColor: "#f8f9ff" } }}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color, fontSize: "1.1rem" }}>{title}</Typography>
        <Box sx={{ height: "3px", width: "40px", backgroundColor: color, borderRadius: "2px", mt: 0.5 }} />
      </Box>
      <Typography variant="h4" sx={{ color: isDynamic ? getQualityScoreColor(value) : color, fontWeight: 700, fontSize: "2rem" }}>{value}</Typography>
    </Box>
  );

  return (
    <div>
      <div className="flex w-full">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, mb: 2 }}>
          <SimpleStatCard title="DQ Score"           value={qualityScore || 0}     color="#bb1ccc" isDynamic />
          <SimpleStatCard title="Rules Processed"    value={totalRecords || 0}     color="#64748b" />
          <SimpleStatCard title="Rules Passed"       value={passedRecords || 0}    color="#10b981" />
          <SimpleStatCard title="Rules Failed"       value={completedRecords || 0} color="#ef4444" />
          <SimpleStatCard title="Rules Not Executed" value={notRunRecords || 0}    color="#f59e0b" />
        </Box>
      </div>

      <div className="flex w-full items-stretch">
        <div className="w-1/2">
          <Box sx={{ backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", padding: 3, border: "1px solid #d3d3d3", marginBottom: 2, height: "100%" }}>
            <Typography variant="h6" sx={{ color: "#bb1ccc", fontWeight: 700, fontSize: "1.1rem", marginBottom: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: "4px", height: "20px", backgroundColor: "#bb1ccc", borderRadius: "2px" }} />
              Dimension Level Score
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 3, maxWidth: "100%" }}>
              {weightage &&
                Object.entries(weightage)
                  .filter(([key]) => key.endsWith("_weightage"))
                  .map(([key, value]) => {
                    const dimensionName  = key.replace("_weightage", "");
                    const score          = results?.latest_dimension_scores?.[dimensionName] || 0;
                    const weightageValue = value;
                    const contribution   = score > 0 && weightageValue > 0 ? (score * weightageValue / 100).toFixed(2) : 0;
                    return (
                      <Box key={key} sx={{ backgroundColor: weightageValue > 0 ? "#f0f9ff" : "#e5e2e2", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", padding: 3, border: "1px solid", borderColor: weightageValue > 0 ? "#e0f2fe" : "#e5e2e2", transition: "all 0.2s ease", "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.12)", transform: "translateY(-2px)" } }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem", color: "#1f2937", marginBottom: 2, textTransform: "uppercase", textAlign: "center" }}>
                          {dimensionName.charAt(0).toUpperCase() + dimensionName.slice(1)}
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          {[
                            ["Score",        score,               score > 0 ? "#059669" : "#dc2626"],
                            ["Weightage",    `${weightageValue}%`, "#374151"],
                            ["Contribution", contribution,        score > 0 ? "#059669" : "#dc2626"],
                          ].map(([label, val, color]) => (
                            <Box key={label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#6b7280" }}>{label}</Typography>
                              <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color }}>{val}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
            </Box>
          </Box>
        </div>

        <div className="w-1/2 pl-4">
          <Box className="shadow-xl bg-white p-4 rounded" sx={{ border: "2px solid #f5f5f5", padding: "16px", width: "100%", height: "100%", minHeight: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", mb: 1 }}>DQ Score Trend</Typography>
            <Box sx={{ flexGrow: 1, display: "flex", alignItems: "stretch" }}>
              {chartData?.data?.[0] ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={transformChartData(chartData)} margin={{ top: 20, right: 40, left: 10, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="displayDate" angle={-30} textAnchor="end" height={80} tick={{ fontSize: 12 }} label={{ value: chartData.layout?.xaxis?.title || "Execution Date", position: "insideBottom", offset: -50, style: { fontSize: 14 } }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} label={{ value: chartData.layout?.yaxis?.title || "Quality Score", angle: -90, position: "insideLeft", style: { fontSize: 14 } }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="score" stroke="#6366F1" strokeWidth={2} dot={{ fill: "#6366F1", r: 4 }} name="Quality Score" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", color: "#999" }}>
                  No chart data available
                </Box>
              )}
            </Box>
          </Box>
        </div>
      </div>

      <div className="mt-8 mb-20">
        <div className="flex items-center justify-between mb-2 ml-2">
          <span className="text-sm text-gray-500">
            {Math.min(visibleCount, filteredAndSortedData.length)} of {filteredAndSortedData.length} result{filteredAndSortedData.length !== 1 ? "s" : ""}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => scrollTable("left")}
              className="flex items-center justify-center w-[56px] h-7 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition-colors shadow-sm"
              title="Scroll left"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => scrollTable("right")}
              className="flex items-center justify-center w-[56px] h-7 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition-colors shadow-sm"
              title="Scroll right"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg">
          <div
            ref={tableScrollRef}
            className="overflow-x-auto rounded-lg"
            style={{ overflowY: "auto", maxHeight: "560px" }}
          >
            <table className="w-full">
              <thead
                className="text-[#404040] font-normal"
                style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#F0FDFA" }}
              >
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className="px-3 pb-[6px] pr-[2px] text-[12px] pt-[10px] text-left whitespace-nowrap w-[14%] select-none font-normal cursor-pointer"
                      style={col.minWidth ? { minWidth: col.minWidth } : {}}
                    >
                      <span className="flex items-center gap-1">
                        {col.label}
                        {sortColumn === col.key
                          ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)
                          : <ArrowUp size={12} className="opacity-30" />}
                      </span>
                    </th>
                  ))}
                </tr>
                <tr>
                  {columns.map((col) => (
                    <th key={`search-${col.key}`} className="px-3 pt-0 pb-[10px] font-normal">
                      <input
                        className="w-full text-[11px] pb-[5px] pl-[10px] placeholder-gray-400 bg-white rounded py-2 outline-none font-normal placeholder:font-normal"
                        placeholder={
                          col.key === "is_cde"    ? "Yes/No"    :
                          col.key === "dq_result" ? "Pass/Fail" :
                          "Search"
                        }
                        value={columnSearch[col.key] || ""}
                        onChange={(e) => handleColumnSearchChange(col.key, e.target.value)}
                      />
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 bg-white">
                {currentData.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="py-8 text-center text-gray-500">
                      No rules found matching your search criteria
                    </td>
                  </tr>
                ) : (
                  currentData.map((rule, index) => (
                    <tr key={rule.rule_name || index} className="hover:bg-gray-50 transition-colors">
                      <td className="py-2 px-3 text-[11px]">
                        <div className="font-medium text-gray-900">
                          {rule.check_type === "Asset Level Check" ? "Asset Level Rule" : rule.column_name}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-[11px]">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${(rule.is_cde === "N/A" || rule.is_cde === false) ? "bg-gray-100 text-gray-600" : "bg-green-100 text-green-700"}`}>
                          {(rule.is_cde === "N/A" || rule.is_cde === false) ? "No" : "Yes"}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-[11px] text-gray-600 max-w-[300px] break-words">
                        {rule.rule_description}
                      </td>
                      <td className="py-2 px-3 text-[11px] text-gray-600 capitalize">
                        {rule.dimension_name.charAt(0).toUpperCase() + rule.dimension_name.slice(1)}
                      </td>
                      <td className="py-2 px-3 text-[11px] text-gray-600 capitalize">
                        {rule.keywords}
                      </td>
                      <td className="py-2 px-3 text-[11px]">
                        {getStatusBadge(rule.dq_result)}
                      </td>
                      <td className="py-2 px-3 text-[11px] font-semibold" style={{ color: getQualityScoreColor(rule.quality_score) }}>
                        {rule.quality_score}%
                      </td>
                      <td className="py-2 px-3 text-[11px] text-gray-600 capitalize">
                        {rule.arguments}
                      </td>
                      <td className="py-2 px-3 text-[11px] text-gray-600" style={{ minWidth: "180px" }}>
                        {rule.sql || "N/A"}
                      </td>
                    </tr>
                  ))
                )}

                {/* Sentinel inside the scrollable container */}
                <tr>
                  <td colSpan={columns.length} className="p-0">
                    <div ref={sentinelRef} style={{ height: 1 }} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div className="flex items-center justify-center py-4 gap-2 border-t border-gray-100">
              <img src={heartLoading} alt="Loading more..." style={{ width : 200}} />
              <span className="text-gray-500 text-2xl">Loading more...</span>
            </div>
          )}
          {!hasMore && filteredAndSortedData.length > 0 && (
            <div className="text-center py-3 text-gray-400 text-xs border-t border-gray-100">
              All {filteredAndSortedData.length} rows loaded
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
